import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  ProfileStoreService,
  UserValidation,
} from '@customer-portal/data-access/settings';
import { getRouteDataByPath } from '@customer-portal/shared/constants';

import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root',
})
export class UserTelemetryService {
  private loggingService = inject(LoggingService);
  private profileStoreService = inject(ProfileStoreService);
  private router = inject(Router);
  private isTrackingInitialized = false;
  private lastLoggedUrl = '';

  initializeUserTracking(
    userValidationData?: UserValidation,
  ): Observable<boolean> {
    try {
      const profile = this.profileStoreService.profileInformation();

      if (profile?.email && profile?.veracityId) {
        this.loggingService.setUserContext(profile.email, profile.veracityId);

        this.setupPageViewTracking();

        return of(true);
      }

      if (userValidationData?.isDnvUser) {
        this.loggingService.setUserContext(
          userValidationData.userEmail,
          userValidationData.veracityId,
        );

        return of(true);
      }

      this.loggingService.logException(
        new Error('Profile email not available for telemetry'),
      );

      return of(false);
    } catch (error) {
      this.loggingService.logException(
        error instanceof Error
          ? error
          : new Error('Error initializing user tracking'),
      );

      return of(false);
    }
  }

  private setupPageViewTracking(): void {
    if (this.isTrackingInitialized) {
      return;
    }

    this.isTrackingInitialized = true;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.logCurrentPageView();
      });
  }

  private logCurrentPageView(): void {
    const currentUrl = window.location.href;
    const { pathname } = window.location;

    if (pathname === '/') {
      return;
    }

    if (this.lastLoggedUrl === currentUrl) {
      return;
    }

    this.lastLoggedUrl = currentUrl;

    const pathSegment = pathname.substring(1);
    const routeDataByPath = getRouteDataByPath(pathSegment);

    if (!routeDataByPath) {
      return;
    }

    const pageTitle = this.getRouteTitle() || routeDataByPath.title;

    const pageViewProperties = {
      pageViewRequest: routeDataByPath.pageViewRequest,
      i18nKey: routeDataByPath.i18nKey,
      fullPath: pathname,
    };

    this.loggingService.logPageView(pageTitle, currentUrl, pageViewProperties);
  }

  private getRouteTitle(): string | undefined {
    const { routerState } = this.router;
    let route = routerState.root;

    if (!route.firstChild) {
      return undefined;
    }

    let title: string | undefined;

    while (route) {
      if (route.snapshot.title) {
        title = route.snapshot.title;
      }

      const nextRoute = route.firstChild;

      if (!nextRoute) {
        break;
      }

      route = nextRoute;
    }

    return title;
  }
}
